import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { isApiError } from '@/api/client'
import { getScenery, putScenery, resetScenery, sceneryQuery, stageKeys, suggestScenery, type SceneryItem } from '@/api/stages'
import { toast } from '@/components/ui/toast'

import { sameItems } from './scenery'
import { useSceneryDraft, type SceneryDraft } from './useSceneryDraft'

/**
 * Everything the Scenery tab (and the header's "Try it") needs about one background's scenery:
 * the saved layout, the automatic example when there is none, the unsaved draft, and the three
 * writes — Save (`PUT` with `base_rev`), Back to automatic (`DELETE`) and Start from suggestion
 * (`GET …/suggest?seed=`, a new seed each time). A `409` on save keeps the draft and asks: use
 * theirs, or keep mine (re-sent on top of the newer revision).
 */

export interface SceneryEditor {
  /** The saved layout is loading / could not be read. */
  pending: boolean
  error: unknown
  mode: 'auto' | 'custom'
  /** What the editor shows: the draft, else the saved layout, else the automatic example. */
  shown: SceneryItem[]
  /** Showing the automatic example (nothing saved, nothing changed). */
  showingExample: boolean
  exampleLoading: boolean
  draft: SceneryDraft
  dirty: boolean
  conflict: boolean
  saving: boolean
  resetting: boolean
  suggesting: boolean
  save(): void
  keepMine(): void
  useTheirs(): void
  backToAuto(): Promise<void>
  startFromSuggestion(): void
  retry(): void
}

export function useSceneryEditor(uid: string): SceneryEditor {
  const qc = useQueryClient()
  const layout = useQuery(sceneryQuery(uid))
  const auto = layout.data?.mode !== 'custom'
  const example = useQuery({
    queryKey: [...stageKeys.scenery(uid), 'example'],
    queryFn: ({ signal }) => suggestScenery(uid, undefined, signal),
    enabled: uid.length > 0 && layout.isSuccess && auto,
    staleTime: 5 * 60_000,
    retry: false,
  })
  const draft = useSceneryDraft()
  const [conflict, setConflict] = useState(false)

  const saved = layout.data?.items ?? []
  const shown = draft.items ?? (auto ? (example.data ?? []) : saved)
  const dirty = draft.items !== null && (auto || !sameItems(draft.items, saved))

  const afterWrite = () => {
    // Cards show "Auto" / "Custom · n".
    void qc.invalidateQueries({ queryKey: [...stageKeys.all(), 'list'] })
    void qc.invalidateQueries({ queryKey: stageKeys.one(uid) })
  }

  const save = useMutation({
    mutationFn: async (opts: { onTop?: boolean }) => {
      const items = draft.items ?? shown
      const base = opts.onTop ? (await getScenery(uid)).rev : (layout.data?.rev ?? 0)
      return putScenery(uid, base, items)
    },
    onSuccess: (next) => {
      qc.setQueryData(stageKeys.scenery(uid), next)
      draft.discard()
      setConflict(false)
      afterWrite()
      toast.success('Scenery saved', { description: 'New stories on this background use your layout.' })
    },
    onError: (err) => {
      if (isApiError(err) && err.isConflict) {
        setConflict(true)
        return
      }
      toast.error('The scenery could not be saved', { description: err instanceof Error ? err.message : undefined })
    },
  })

  const reset = useMutation({
    mutationFn: () => resetScenery(uid, layout.data?.rev),
    onSuccess: (next) => {
      if (next) qc.setQueryData(stageKeys.scenery(uid), next)
      void qc.invalidateQueries({ queryKey: stageKeys.scenery(uid) })
      draft.discard()
      setConflict(false)
      afterWrite()
      toast.success('Back to automatic scenery', { description: 'Each story picks its own again.' })
    },
    onError: (err) => {
      if (isApiError(err) && err.isConflict) {
        // Someone saved a newer layout: show it, and let the parent decide again.
        void layout.refetch()
        toast.error('Someone changed this scenery a moment ago', { description: 'Their layout was loaded. Press “Back to automatic” again if you still want it.' })
        return
      }
      toast.error('Could not go back to automatic', { description: err instanceof Error ? err.message : undefined })
    },
  })

  const suggest = useMutation({
    mutationFn: () => suggestScenery(uid, Math.floor(Math.random() * 1_000_000) + 1),
    onSuccess: (items) => {
      draft.set(items)
      if (items.length === 0) toast.info('The studio found nothing to suggest for this place.')
    },
    onError: (err) => toast.error('No suggestion this time', { description: err instanceof Error ? err.message : undefined }),
  })

  return {
    pending: layout.isPending,
    error: layout.error,
    mode: auto ? 'auto' : 'custom',
    shown,
    showingExample: auto && draft.items === null,
    exampleLoading: auto && draft.items === null && example.isFetching,
    draft,
    dirty,
    conflict,
    saving: save.isPending,
    resetting: reset.isPending,
    suggesting: suggest.isPending,
    save: () => save.mutate({}),
    keepMine: () => save.mutate({ onTop: true }),
    useTheirs: () => {
      draft.discard()
      setConflict(false)
      void layout.refetch()
    },
    backToAuto: async () => {
      await reset.mutateAsync().catch(() => undefined)
    },
    startFromSuggestion: () => suggest.mutate(),
    retry: () => void layout.refetch(),
  }
}
